import React, { useState, useEffect, useRef } from "react";

const HoverCarousel = ({ images = [], alt = "Product Image", className = "h-48" }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isHovered, setIsHovered] = useState(false);
    const [isInteracting, setIsInteracting] = useState(false); // new flag
    const intervalRef = useRef(null);

    // Handle auto-slide on hover
    useEffect(() => {
        if (isHovered && !isInteracting && images.length > 1) {
            intervalRef.current = setInterval(() => {
                setCurrentIndex(prev => (prev + 1) % images.length);
            }, 2000);
        } else {
            clearInterval(intervalRef.current);
        }

        return () => clearInterval(intervalRef.current);
    }, [isHovered, isInteracting, images.length]);

    const prevImage = () => {
        setCurrentIndex(prev => (prev - 1 + images.length) % images.length);
        setIsInteracting(true); // stop auto-slide when user clicks
    };

    const nextImage = () => {
        setCurrentIndex(prev => (prev + 1) % images.length);
        setIsInteracting(true); // stop auto-slide when user clicks
    };

    // If user leaves hover, reset interaction after a short delay
    const handleMouseLeave = () => {
        setIsHovered(false);
        setTimeout(() => setIsInteracting(false), 3000); // reset interaction after 3s
    };

    if (!images || images.length === 0) {
        return (
            <div className={`w-full ${className} flex items-center justify-center bg-gray-100 text-gray-400`}>
                No Image
            </div>
        );
    }

    return (
        <div
            className={`relative w-full overflow-hidden ${className}`}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={handleMouseLeave}
        >
            <div className="overflow-hidden relative w-full h-48">
                <div
                    className="flex transition-transform duration-500 ease-in-out"
                    style={{ transform: `translateX(-${currentIndex * 100}%)` }}
                >
                    {images.map((img, idx) => (
                        <img
                            key={idx}
                            src={img}
                            alt={alt}
                            className="w-full flex-shrink-0 object-cover object-center"
                        />
                    ))}
                </div>
            </div>

            {/* Prev/Next Buttons */}
            {images.length > 1 && (
                <>
                    <button
                        onClick={prevImage}
                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-40 text-white p-2 rounded-full hover:bg-opacity-60 transition"
                    >
                        ‹
                    </button>
                    <button
                        onClick={nextImage}
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-40 text-white p-2 rounded-full hover:bg-opacity-60 transition"
                    >
                        ›
                    </button>
                </>
            )}

            {/* Dots Indicator */}
            {images.length > 1 && (
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                    {images.map((_, idx) => (
                        <span
                            key={idx}
                            className={`w-2 h-2 rounded-full ${idx === currentIndex ? "bg-white" : "bg-gray-400"}`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default HoverCarousel;
